/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/// <reference path="types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

/* tslint:disable: no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { reaction, when } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { IDobbiesStoreSelectorData } from './dobbies-store-selector.data';
import {
    IDobbiesStoreSelectorProps,
    mode as modeEnum,
    searchRadiusUnit,
    style as styleEnum
} from './dobbies-store-selector.props.autogenerated';

import {
    StoreSelectorEmptyMessage,
    StoreSelectorFoundLocationsMessage,
    StoreSelectorLocationLines,
    StoreSelectorSearchForm,
    StoreSelectorTermsOfService,
    StoreSelectorWaiting
} from './components';

import {
    getFullAvailableInventoryNearby,
    GetFullAvailableInventoryNearbyInput,
    getOrgUnitLocationsByArea,
    GetOrgUnitLocationsByAreaInput,
    IFullOrgUnitAvailability
} from '@msdyn365-commerce-modules/retail-actions';

import { IStoreInfo } from '@msdyn365-commerce-modules/bopis-utilities';
import { getAllOrgUnitLocationsByArea, GetAllOrgUnitLocationsByAreaInput } from '../../actions/get-all-store-location-information';
import {
    getTelemetryObject,
    IModuleProps,
    INodeProps,
    ITelemetryContent,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    NodeTag
} from '@msdyn365-commerce-modules/utilities';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
export interface IStoreSelectorState {
    isSearchInProgress: boolean;
    searchTerm?: string;
    isSeeAllStoreValue: boolean;
    isLocationDisabled?: boolean;
    sortByName: boolean;
    latitudeVal: number;
    longitudeVal: number;
    radiusVal: number;
    allStore: IFullOrgUnitAvailability[];
}

export interface IStoreSelectorViewProps extends IDobbiesStoreSelectorProps<IDobbiesStoreSelectorData> {
    state: IStoreSelectorState;

    // Containers:
    ModuleProps: IModuleProps;
    BodyContainerProps: INodeProps;
    FooterContainerProps: INodeProps;
    HeaderContainerProps: INodeProps;
    HeaderProps?: React.ReactNode;

    // Components
    locationsMessage?: React.ReactNode;
    search?: React.ReactNode;
    spinner?: React.ReactNode;
    locationsList?: React.ReactNode;
    noLocationsMessage?: React.ReactNode;
    terms?: React.ReactNode;
    headerText?: React.ReactNode;

    callbacks: {
        closeDialog(): Promise<void>;
        searchTermChanged(searchTerm: string): Promise<void>;
        performSearch(searchTerm: string): Promise<void>;
        selectedLocation(location: OrgUnitLocation | undefined): Promise<void>;
        getAndUpdateUserLocation(): Promise<void>;
        attachMapAutoSuggest(): Promise<void>;
        onSuggestionSelected(result: Microsoft.Maps.ISuggestionResult): Promise<void>;
        setPreferredStore(storeId: string | undefined): Promise<void>;
        removePreferredStore(): Promise<void>;
        setSelectedStore(storeId: string | undefined): Promise<void>;
        setOrgUnitStoreInformation(location: OrgUnitLocation | undefined): Promise<void>;
    };
}

/**
 *
 * Pick Up In Store module
 * @extends React.Component<IPickUpInStoreProps<IPickUpInStoreData>>}
 */
@observer
class StoreSelector extends React.Component<IDobbiesStoreSelectorProps<IDobbiesStoreSelectorData>, IStoreSelectorState> {
    private autoSuggestManager: Microsoft.Maps.AutosuggestManager | undefined;
    private telemetryContent: ITelemetryContent;

    constructor(props: IDobbiesStoreSelectorProps<IDobbiesStoreSelectorData>) {
        super(props);
        this.state = {
            isSearchInProgress: true,
            searchTerm: '',
            isSeeAllStoreValue: false,
            sortByName: false,
            latitudeVal: 0,
            longitudeVal: 0,
            radiusVal: 1,
            allStore: []
        };
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.sortLocations = this.sortLocations.bind(this);
    }

    public sortLocations() {
        this.setState({
            sortByName: !this.state.sortByName
        });
    }

    // tslint:disable-next-line: max-func-body-length
    public async componentDidMount(): Promise<void> {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            config: { autoSuggestionEnabled }
        } = this.props;

        // Adding Timeout to make sure it loads the data based upon the preferred store.
        setTimeout(() => {
            this._getAndUpdateStoreLocation();
        }, 0);

        reaction(
            () => this.props.data.storeSelectorStateManager.result && this.props.data.storeSelectorStateManager.result.isDialogOpen,
            async () => {
                const context = this.props.data.storeSelectorStateManager.result?.context;
                if (context?.id === this.props.id) {
                    this._getAndUpdateStoreLocation();
                }
            }
        );

        reaction(
            () =>
                this.props.data.storeSelectorStateManager.result &&
                this.props.data.storeSelectorStateManager.result.context &&
                this.props.data.storeSelectorStateManager.result.context.id,
            async () => {
                const context = this.props.data.storeSelectorStateManager.result?.context;
                if (!context || context?.id === this.props.id) {
                    await this._searchTermChanged('');
                    this._getAndUpdateStoreLocation();
                }
            }
        );

        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }

        if (this.props.data.storeSelectorStateManager.result?.loadMapApi) {
            this.props.data.storeSelectorStateManager.result
                ?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode,
                    market: this.props.context?.actionContext?.requestContext?.channel?.DefaultLanguageId
                })
                .then()
                .catch();
        }

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.loadMapApi,
            () => {
                this.props.data.storeSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode,
                    market: this.props.context?.actionContext?.requestContext?.channel?.DefaultLanguageId
                });
            }
        );

        if (autoSuggestionEnabled) {
            when(
                () => {
                    const isMapApiLoaded = !!this.props.data.storeSelectorStateManager.result?.isMapApiLoaded;
                    const isDialogOpen = !!this.props.data.storeSelectorStateManager.result?.isDialogOpen;
                    const isSameContext = this.props.data.storeSelectorStateManager.result?.context?.id === this.props.id;
                    return isMapApiLoaded && isDialogOpen && isSameContext;
                },
                async () => {
                    setTimeout(async () => {
                        await this._attachMapAutoSuggest();
                    }, 0);
                }
            );
        }

        await this._getAllStores(31.5070767, 74.4315609, 0, 0);
    }

    public shouldComponentUpdate(nextProps: IDobbiesStoreSelectorProps<IDobbiesStoreSelectorData>): boolean {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        if (context && context.id === this.props.id) {
            return true;
        }
        return false;
    }

    // tslint:disable-next-line:max-func-body-length cyclomatic-complexity
    public render(): JSX.Element {
        const {
            config: { termsOfServiceLink, className, heading, mode, style, setAsPreferredStore, showAllStores, lookupRadius },
            context: {
                telemetry,
                app: { config: appConfig },
                actionContext: {
                    requestContext: { channel, cookies }
                }
            },
            resources,
            data: {
                availabilitiesWithHours: { result: availabilitiesWithHours },
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeLocations: { result: storeLocations }
            }
        } = this.props;
        /* VSI-Customization -- START Make URL and pass it down to component  */
        const sitePath = this.props.context.request.sitePath;
        const urlOrigin = this.props.context.request.url.requestUrl.origin;
        const url = urlOrigin.concat((sitePath && `${sitePath}/`) || '/');
        /* VSI-Customization -- END  */

        const isMapDisabled = channel && channel.BingMapsApiKey && channel.BingMapsEnabled ? false : true;
        const context = storeSelectorStateManager ? storeSelectorStateManager.context : undefined;
        const isDialogOpen: boolean = storeSelectorStateManager
            ? storeSelectorStateManager.isDialogOpen && context?.id === this.props.id
            : false;
        const styleDefault = style !== undefined ? style : styleEnum.dialog;
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;
        const inlineView = styleDefault === styleEnum.inline;
        const locatorView = modeDefault === modeEnum.findStores;
        const hideInventory = locatorView;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;
        const preferredStore: IStoreInfo | undefined = storeSelectorStateManager?.preferredStore;
        const displayList = storeSelectorStateManager?.listMapViewState?.displayList;
        const isPreferredStoreEnabled = setAsPreferredStore && cookies?.isConsentGiven();
        const showLocationView =
            context &&
            (context.showMapViewLink === undefined || context.showMapViewLink) &&
            locatorView &&
            storeSelectorStateManager?.isMapModuleLoaded;

        const StoreSelectorActions = {
            toggleListMapViewState(): void {
                storeSelectorStateManager?.toggleListMapViewState();
            }
        };

        if (!(channel && channel.BingMapsApiKey)) {
            telemetry.error('BingMapsApiKey is missing.');
        }

        const configHeading: string | any = heading
            ? heading
            : locatorView
            ? resources.storeLocatorHeaderText
            : resources.storeSelectorHeaderText;
        const preferredDistanceUnit =
            this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? resources.kilometersShortText : resources.milesShortText;

        const viewProps = {
            ...(this.props as IDobbiesStoreSelectorProps<IDobbiesStoreSelectorData>),
            state: this.state,

            ModuleProps: {
                tag: inlineView ? ('div' as NodeTag) : Modal,
                moduleProps: this.props,
                className: classname(
                    `ms-store-${inlineView ? 'locate' : 'select'}`,
                    className,
                    inlineView ? '' : 'msc-modal-input-required'
                ),
                autoFocus: true,
                fade: true,
                isOpen: isDialogOpen,
                onClosed: this._closeDialog,
                horizontalPosition: 'center',
                verticalPosition: 'center',
                toggle: this._closeDialog
            },
            HeaderContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalHeader,
                className: `ms-store-select__${inlineView ? 'header' : 'modal-header'}`,
                toggle: this._closeDialog
            },
            HeaderProps: configHeading && (
                <Msdyn365.Text
                    className='msc-modal__title'
                    {...configHeading}
                    tag={'h2'}
                    text={configHeading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            FooterContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalFooter,
                className: `ms-store-select__${inlineView ? 'footer' : 'modal-footer'} ${!displayList ? 'map-only-view' : ''}`
            },
            BodyContainerProps: {
                tag: inlineView ? ('div' as NodeTag) : ModalBody,
                className: `ms-store-select__${inlineView ? 'body' : 'modal-body'}`
            },
            locationsMessage: !this.state.isLocationDisabled && (
                <StoreSelectorFoundLocationsMessage
                    resources={resources}
                    locations={hideInventory ? storeLocations : availabilitiesWithHours}
                    searchRadius={lookupRadius || 50}
                    preferredDistanceUnit={preferredDistanceUnit}
                    isSeeAllStore={this.state.isSeeAllStoreValue}
                />
            ),
            search: (
                <StoreSelectorSearchForm
                    value={this.state.searchTerm || ''}
                    resources={resources}
                    performSearch={this._performSearch}
                    searchTermChanged={this._searchTermChanged}
                    isMapDisabled={isMapDisabled}
                    showAllStores={showAllStores}
                    id={this.props.id}
                    sortLocations={this.sortLocations}
                    sortByName={this.state.sortByName}
                    // tslint:disable-next-line:react-this-binding-issue jsx-no-lambda
                    onShowAllStores={() => this._getAndUpdateStoreLocation(true)}
                    onToggleListMapViewState={StoreSelectorActions.toggleListMapViewState}
                    displayList={displayList}
                    locatorView={showLocationView}
                    productPickUpDeliveryOptions={storeSelectorStateManager?.context?.deliveryOptions?.DeliveryOptions}
                    telemetryContent={this.telemetryContent}
                />
            ),
            spinner: <StoreSelectorWaiting />,
            locationsList: ((availabilitiesWithHours && availabilitiesWithHours.length) || (storeLocations && storeLocations.length)) && (
                <StoreSelectorLocationLines
                    locations={hideInventory ? storeLocations : availabilitiesWithHours}
                    resources={resources}
                    preferredDistanceUnit={preferredDistanceUnit}
                    alreadySelectedLocation={context ? context.alreadySelectedLocation : undefined}
                    outOfStockThreshold={appConfig.outOfStockThreshold || 0}
                    enableOutOfStockCheck={appConfig.enableStockCheck || false}
                    onLocationSelected={this._onOrgUnitLocationSelected}
                    storeLocatorView={locatorView}
                    selectedStoreLocationId={selectedStoreLocationId}
                    onClick={this._setSelectedStore}
                    preferredStoreLocationId={preferredStore?.StoreId ? preferredStore.StoreId : ''}
                    onSetAsPreferredStore={this._setPreferredStore}
                    onRemovePreferredStore={this._removePreferredStore}
                    isPreferredStoreEnabled={isPreferredStoreEnabled}
                    displayList={displayList}
                    telemetryContent={this.telemetryContent}
                    isLocationDisabled={this.state.isLocationDisabled}
                    productPickUpDeliveryOptions={storeSelectorStateManager?.context?.deliveryOptions?.DeliveryOptions}
                    url={url}
                    linkText={this.props.resources.storeLinksText}
                    sortByName={this.state.sortByName}
                    viewProps={this.props}
                />
            ),
            noLocationsMessage: <StoreSelectorEmptyMessage resources={resources} />,
            terms: (
                <StoreSelectorTermsOfService
                    link={termsOfServiceLink}
                    onTextChange={this.handleLinkTextChange}
                    requestContext={this.props.context.request}
                />
            ),
            // Not being used in current context, we can use it here to pass more props.
            headerText: (
                <span>{heading ? heading : locatorView ? resources.storeLocatorHeaderText : resources.storeSelectorHeaderText}</span>
            ),

            callbacks: {
                sortLocations: this.sortLocations,
                searchTermChanged: this._searchTermChanged,
                performSearch: this._performSearch,
                closeDialog: this._closeDialog,
                selectedLocation: this._onOrgUnitLocationSelected,
                getAndUpdateUserLocation: () => {
                    this._getAndUpdateStoreLocation();
                    return Promise.resolve();
                },
                attachMapAutoSuggest: this._attachMapAutoSuggest,
                onSuggestionSelected: this._onOrgUnitLocationSelected,
                setPreferredStore: this._setPreferredStore,
                removePreferredStore: this._removePreferredStore,
                setSelectedStore: this._setSelectedStore,
                setOrgUnitStoreInformation: this._setOrgUnitStoreInformation
            }
        } as IStoreSelectorViewProps;

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleLinkTextChange = (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.termsOfServiceLink) {
            this.props.config.termsOfServiceLink.linkText = event.target.value;
        }
    };
    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading = event.target.value);

    private _attachMapAutoSuggest = async (): Promise<void> => {
        const {
            config: { autoSuggestOptions },
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager?.isMapApiLoaded && !this.autoSuggestManager) {
            Microsoft.Maps.loadModule('Microsoft.Maps.AutoSuggest', {
                callback: () => {
                    const options = { ...autoSuggestOptions, countryCode: channel?.ChannelCountryRegionISOCode };
                    this.autoSuggestManager = new Microsoft.Maps.AutosuggestManager(options);
                    this.autoSuggestManager.attachAutosuggest(
                        `#ms-store-select__search-box_${this.props.id}`,
                        `#ms-store-select__search-box-container_${this.props.id}`,
                        this._onSuggestionSelected
                    );
                },
                errorCallback: () => {
                    if (telemetry) {
                        telemetry.debug('Unable to attach map auto suggest.');
                    }
                },
                credentials: channel?.BingMapsApiKey
            });
        }
    };

    private _onSuggestionSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
        const context =
            this.props.data.storeSelectorStateManager &&
            this.props.data.storeSelectorStateManager.result &&
            this.props.data.storeSelectorStateManager.result?.context;
        if (context && context?.id === this.props.id) {
            await this._searchTermChanged(result.formattedSuggestion);
            await this._performSearch(result.formattedSuggestion);
        }
    };

    private _setPreferredStore = async (storeId: string | undefined): Promise<void> => {
        if (!storeId) {
            return;
        }

        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            config: { mode },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager },
                storeLocations: { result: storeLocations },
                availabilitiesWithHours: { result: availabilitiesWithHours }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;

        let stores: IFullOrgUnitAvailability[];
        if (modeDefault === modeEnum.findStores && storeLocations) {
            stores = storeLocations;
        } else {
            stores = availabilitiesWithHours!;
        }

        const preferedStore = stores.find(store => store.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitNumber === storeId);

        await storeSelectorStateManager.setPreferredStoreLocation(
            storeId,
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.OrgUnitName,
            cookies?.isConsentGiven(),
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.Latitude,
            preferedStore?.OrgUnitAvailability?.OrgUnitLocation?.Longitude
        );
    };

    private _removePreferredStore = async (): Promise<void> => {
        const {
            context: {
                actionContext: {
                    requestContext: { cookies }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        await storeSelectorStateManager.clearPreferredStore(cookies?.isConsentGiven());
    };

    private _setSelectedStore = async (storeId: string | undefined): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        await storeSelectorStateManager.setSelectedStoreLocationId(storeId);
    };

    private _performSearch = async (searchTerm: string): Promise<void> => {
        const {
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (!searchTerm || !storeSelectorStateManager) {
            return;
        }

        this.setState({ isSearchInProgress: true, isSeeAllStoreValue: false });

        /* VSI-Customization -- Filter out store zip code  */
        const filteredStore: any = this.state.allStore?.filter(
            (str: any) =>
                (str?.OrgUnitAvailability?.OrgUnitLocation.OrgUnitName?.toLowerCase()?.includes(searchTerm.trim()?.toLowerCase()) ||
                    searchTerm
                        .trim()
                        ?.toLowerCase()
                        ?.includes(str?.OrgUnitAvailability?.OrgUnitLocation.OrgUnitName?.toLowerCase())) &&
                str?.OrgUnitAvailability?.OrgUnitLocation.OrgUnitNumber !== 'S096CNC'
        );
        const searchStoreStr = filteredStore?.length >= 1 ? filteredStore[0]?.OrgUnitAvailability?.OrgUnitLocation.Zip : searchTerm;
        const location = await storeSelectorStateManager.geoLocate(searchStoreStr.trim() !== '' ? searchStoreStr : '0', actionContext);
        /* VSI-Customization -- END  */

        if (location) {
            await this._updateLocations(location.latitude, location.longitude);
        } else {
            await this._updateLocations();
        }

        this.setState({ isSearchInProgress: false });
    };

    private _searchTermChanged = async (searchTerm: string): Promise<void> => {
        this.setState({ searchTerm: searchTerm });
    };

    private _getAndUpdateStoreLocation = (showAllStores?: boolean): void => {
        const {
            config: { style },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const styleDefault = style !== undefined ? style : styleEnum.dialog;

        if (styleDefault !== styleEnum.inline && (!storeSelectorStateManager || !storeSelectorStateManager.isDialogOpen)) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }

        this.setState({ isSearchInProgress: true, isSeeAllStoreValue: false });

        // Try using cached location first
        const cachedLocationString = localStorage.getItem('userLocation');
        let cachedLocation: { lat: number; lng: number; timestamp?: number } | null = null;
        const cacheHours = this.props.context.app.config.userLocationCacheTime || 12;
        const CACHE_HOURS_MS = cacheHours * 60 * 60 * 1000;

        try {
            cachedLocation = cachedLocationString ? JSON.parse(cachedLocationString) : null;
        } catch {
            localStorage.removeItem('userLocation');
        }

        // Check if cached location exists and is not older than 12 hours
        const isCacheValid = cachedLocation && cachedLocation.timestamp && Date.now() - cachedLocation.timestamp < CACHE_HOURS_MS;

        // If cached location exists and is valid, use it
        if (cachedLocation && isCacheValid) {
            this.setState({ isLocationDisabled: false });
            if (showAllStores) {
                this.setState({ isSeeAllStoreValue: true });
                this._searchTermChanged('').then(() => {
                    this._updateLocations(cachedLocation!.lat, cachedLocation!.lng, 0).then(() => {
                        this.setState({ isSearchInProgress: false });
                    });
                });
            } else {
                this._updateLocations(cachedLocation.lat, cachedLocation.lng).then(() => {
                    this.setState({ isSearchInProgress: false });
                });
            }
            return;
        }

        // Otherwise get new location from geolocation API
        if (navigator && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async position => {
                    // Cache the location with timestamp
                    localStorage.setItem(
                        'userLocation',
                        JSON.stringify({
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                            timestamp: Date.now()
                        })
                    );

                    this.setState({ isLocationDisabled: false });
                    if (showAllStores) {
                        this.setState({ isSeeAllStoreValue: true });
                        await this._searchTermChanged('');
                        await this._updateLocations(position.coords.latitude, position.coords.longitude, 0);
                    } else {
                        await this._updateLocations(position.coords.latitude, position.coords.longitude);
                    }

                    this.setState({ isSearchInProgress: false });
                },
                async error => {
                    // If the location setting is turned off
                    console.log('Error', error);
                    if (error.PERMISSION_DENIED) {
                        this.setState({ isLocationDisabled: true });
                        if (showAllStores) {
                            this.setState({ isSeeAllStoreValue: true });
                            await this._searchTermChanged('');
                            await this._updateLocations(void 0, void 0, 0);
                        } else {
                            await this._updateLocations();
                        }
                    }

                    this.setState({ isSearchInProgress: false });
                },
                {
                    timeout: 5000
                }
            );
        } else {
            this.setState({ isSearchInProgress: false });
        }
    };

    private _closeDialog = async (): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        this.setState({ searchTerm: '' });

        if (storeSelectorStateManager) {
            return storeSelectorStateManager.closeDialog();
        }
    };

    private _setOrgUnitStoreInformation = async (orgUnitStoreInformation: IFullOrgUnitAvailability[]): Promise<void> => {
        const {
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            },
            config: { style },
            telemetry
        } = this.props;

        if (!storeSelectorStateManager) {
            return;
        }

        const context = storeSelectorStateManager.context;
        const id: string = context && context.id ? context.id : this.props.id;
        const styleDefault = style !== undefined ? style : styleEnum.dialog;
        await storeSelectorStateManager
            .setOrgUnitStoreInformation({
                id,
                showMapViewLink: context && context.showMapViewLink,
                isDialog: styleDefault === styleEnum.dialog,
                orgUnitStoreInformation,
                onLocationSelected: orgUnitLocation => {
                    // TO-DO handle event for when location in selected
                    return Promise.resolve();
                }
            })
            .catch((error: Error) => {
                if (telemetry) {
                    telemetry.error(error.message);
                    telemetry.debug('Unable to Update store location state');
                }
                return;
            });
        return;
    };

    /**
     * Method to update user location.
     * @param latitude - User location.
     * @param longitude - User location.
     * @param radius - Search Criteria Radius.
     * @param distanceUnitValue - Search Criteria distance.
     * @returns Void promise.
     */
    private readonly _getAllStores = async (
        latitude?: number,
        longitude?: number,
        radius?: number,
        distanceUnitValue?: number
    ): Promise<void> => {
        const {
            context: { actionContext }
        } = this.props;

        const orgUnitLocationsInput = new GetAllOrgUnitLocationsByAreaInput(
            latitude,
            longitude,
            radius,
            distanceUnitValue,
            this.state.isLocationDisabled
        );
        const orgUnitLocations = await getAllOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
        if (orgUnitLocations?.length >= 1) {
            this.setState({ allStore: orgUnitLocations });
        }
    };

    // tslint:disable-next-line: cyclomatic-complexity
    private _updateLocations = async (latitude?: number, longitude?: number, radius?: number): Promise<void> => {
        const {
            telemetry,
            config: { mode, lookupRadius, style },
            context: { actionContext },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const styleDefault = style !== undefined ? style : styleEnum.dialog;
        const calRadius = radius || radius === 0 ? radius : lookupRadius || 50;
        const distanceUnitValue = this.props.config.searchRadiusUnit === searchRadiusUnit.kilometers ? 1 : 0;
        const modeDefault = mode !== undefined ? mode : modeEnum.pickupInStore;
        const preferredStore: IStoreInfo | undefined = storeSelectorStateManager?.preferredStore;
        let lat = this.state.searchTerm || !preferredStore?.Latitude ? latitude : preferredStore.Latitude;
        let long = this.state.searchTerm || !preferredStore?.Longitude ? longitude : preferredStore.Longitude;
        if (styleDefault === styleEnum.inline) {
            const lastInlineStoreLocation = storeSelectorStateManager?.lastInlineShownStoreInfo;
            if (lastInlineStoreLocation && !this.state.searchTerm) {
                lat = lastInlineStoreLocation.Latitude ? lastInlineStoreLocation.Latitude : lat;
                long = lastInlineStoreLocation.Longitude ? lastInlineStoreLocation.Longitude : long;
            }
            await storeSelectorStateManager?.setLastInlineStoreInfo(lat, long);
        }

        if (modeDefault === modeEnum.findStores) {
            const orgUnitLocationsInput = new GetOrgUnitLocationsByAreaInput(
                lat,
                long,
                calRadius,
                distanceUnitValue,
                this.state.isLocationDisabled
            );

            const orgUnitLocations = await getOrgUnitLocationsByArea(orgUnitLocationsInput, actionContext);
            if (orgUnitLocations) {
                const tempSortedLocations = orgUnitLocations.sort(
                    (a, b) => a.OrgUnitAvailability!.OrgUnitLocation!.Distance! - b.OrgUnitAvailability!.OrgUnitLocation!.Distance!
                );

                await this._setOrgUnitStoreInformation(tempSortedLocations);
                await this._setSelectedStore(undefined);
            }
            return;
        }

        if (!storeSelectorStateManager || !storeSelectorStateManager.context) {
            // Store selector dialog isn't open yet, so don't try and update anything
            return;
        }

        if (!storeSelectorStateManager.context.product) {
            // TODO: Use alternative search method here to not make inventory check
            return;
        }

        const input = new GetFullAvailableInventoryNearbyInput(
            storeSelectorStateManager.context.product.RecordId,
            lat || 0,
            long || 0,
            calRadius,
            distanceUnitValue,
            this.state.isLocationDisabled
        );

        await getFullAvailableInventoryNearby(input, actionContext).catch(error => {
            telemetry.warning(error);
            telemetry.error(error.message);
            telemetry.debug(`Unable to get full available inventory near by`);
        });
    };

    private _onOrgUnitLocationSelected = async (location: OrgUnitLocation | undefined, deliveryMode?: string): Promise<void> => {
        const {
            telemetry,
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        if (storeSelectorStateManager) {
            if (location && storeSelectorStateManager.context) {
                // Want to eat exceptions here so that the dialog still closes even if the onLocationSelected call goes wrong
                await storeSelectorStateManager.context.onLocationSelected(location, deliveryMode).catch(error => {
                    telemetry.warning(error);
                    telemetry.error(error.message);
                    telemetry.debug(`Unable to get on org unit location selected`);
                });
            }

            await storeSelectorStateManager.closeDialog();
        }
    };
}

export default StoreSelector;
